import { useEffect, useState, useMemo } from "react";
import { apiFetch, API_URL } from "../api/api";

const MONTH_OPTIONS = [
  { label: "Semua Bulan", value: "" },
  { label: "Januari 2026", value: "2026-01" },
  { label: "Februari 2026", value: "2026-02" },
  { label: "Maret 2026", value: "2026-03" },
  { label: "April 2026", value: "2026-04" },
];

function downloadCSV(url: string, filename: string) {
  const token = localStorage.getItem("token");
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => r.blob())
    .then(blob => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
    });
}

export default function WeeklyReport() {
  const [rows, setRows] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [bulan, setBulan] = useState("");

  const [expStart, setExpStart] = useState("2026-01-04");
  const [expEnd, setExpEnd] = useState("2026-04-04");
  const [showExport, setShowExport] = useState(false);

  const perPage = 10;

  useEffect(() => {
    apiFetch("/admin/reports/weekly").then(res => {
      setRows(res.data ?? []);
      setPage(1);
    });
  }, []);

  /* Filter by bulan + search */
  const filtered = useMemo(() => {
    return rows.filter(r => {
      const matchSearch = r.nama.toLowerCase().includes(search.toLowerCase());
      const matchBulan = bulan
        ? r.start_date?.startsWith(bulan)
        : true;
      return matchSearch && matchBulan;
    });
  }, [rows, search, bulan]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * perPage, safePage * perPage);

  const handleExport = () => {
    const params = expStart && expEnd
      ? `?start=${expStart}&end=${expEnd}`
      : "";
    downloadCSV(`${API_URL}/admin/export/weekly${params}`, `laporan-mingguan${params ? `-${expStart}-${expEnd}` : ""}.csv`);
  };

  /* Summary from filtered */
  const summaryBaik = filtered.filter(r => r.status === "Sangat Baik").length;
  const summaryCukup = filtered.filter(r => r.status === "Cukup").length;
  const summaryRendah = filtered.filter(r => r.status === "Perlu Diperbaiki").length;

  return (
    <div className="space-y-6">
      {/* ─── HEADER ─── */}
      <div className="bg-white rounded-2xl shadow p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-purple-700">Laporan Mingguan</h1>
            <p className="text-sm text-gray-500">Rekap kepatuhan mingguan ibu hamil</p>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            {/* Month Filter */}
            <select
              value={bulan}
              onChange={e => { setBulan(e.target.value); setPage(1); }}
              className="border border-purple-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 outline-none bg-white"
            >
              {MONTH_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            {/* Search */}
            <input
              type="text"
              placeholder="Cari nama ibu..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="px-4 py-2 rounded-xl border border-purple-200 focus:ring-2 focus:ring-purple-300 outline-none text-sm"
            />

            {/* Export */}
            <button
              onClick={() => setShowExport(v => !v)}
              className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl text-sm shadow transition"
            >
              <ExcelIcon className="w-4 h-4" />
              Export Excel
            </button>
          </div>
        </div>

        {/* Export Panel */}
        {showExport && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-xs text-gray-600 block mb-1">Periode Mulai</label>
              <input type="date" value={expStart} onChange={e => setExpStart(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-400" />
            </div>
            <div>
              <label className="text-xs text-gray-600 block mb-1">Periode Selesai</label>
              <input type="date" value={expEnd} onChange={e => setExpEnd(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-400" />
            </div>
            <button onClick={handleExport}
              className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-xl text-sm shadow transition">
              Download CSV
            </button>
          </div>
        )}
      </div>

      {/* ─── SUMMARY CHIPS ─── */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-center">
          <p className="text-sm text-green-700 font-medium">Sangat Baik</p>
          <p className="text-3xl font-bold text-green-600">{summaryBaik}</p>
          <p className="text-xs text-gray-400">record</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 text-center">
          <p className="text-sm text-yellow-700 font-medium">Cukup</p>
          <p className="text-3xl font-bold text-yellow-600">{summaryCukup}</p>
          <p className="text-xs text-gray-400">record</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-center">
          <p className="text-sm text-red-700 font-medium">Perlu Diperbaiki</p>
          <p className="text-3xl font-bold text-red-600">{summaryRendah}</p>
          <p className="text-xs text-gray-400">record</p>
        </div>
      </div>

      {/* ─── TABLE ─── */}
      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-purple-50 text-purple-700">
            <tr>
              <Th>No</Th>
              <Th>Nama</Th>
              <Th>Minggu</Th>
              <Th>Periode</Th>
              <Th>Kepatuhan</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((r, i) => (
              <tr key={i} className={`border-t hover:bg-purple-50/40 transition ${i % 2 === 0 ? "" : "bg-gray-50/50"}`}>
                <Td className="text-gray-400 text-center w-10">
                  {(safePage - 1) * perPage + i + 1}
                </Td>
                <Td className="font-medium">{r.nama}</Td>
                <Td>
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                    Minggu {r.minggu_ke}
                  </span>
                </Td>
                <Td className="text-gray-500">{r.start_date} – {r.end_date}</Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-100 rounded-full h-2 w-20">
                      <div
                        className={`h-2 rounded-full ${r.persentase >= 80 ? "bg-green-500" : r.persentase >= 60 ? "bg-yellow-400" : "bg-red-400"}`}
                        style={{ width: `${Math.min(100, r.persentase)}%` }}
                      />
                    </div>
                    <span className="font-semibold text-gray-700">{r.persentase}%</span>
                  </div>
                </Td>
                <Td><StatusBadge status={r.status} /></Td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-12 text-gray-400 italic">
                  Data tidak ditemukan
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ─── PAGINATION ─── */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
        <p className="text-sm text-gray-500">
          Menampilkan <span className="font-semibold text-purple-600">{paginated.length}</span>{" "}
          dari <span className="font-semibold">{filtered.length}</span> record
        </p>
        <div className="flex items-center gap-1">
          <button disabled={safePage === 1} onClick={() => setPage(1)}
            className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-30 hover:bg-purple-50 transition">«</button>
          <button disabled={safePage === 1} onClick={() => setPage(p => Math.max(1, p - 1))}
            className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-30 hover:bg-purple-50 transition">‹</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
            .reduce<(number | "...")[]>((acc, p, idx, arr) => {
              if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("...");
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) =>
              p === "..." ? (
                <span key={`e${i}`} className="px-2 text-gray-400">…</span>
              ) : (
                <button key={p} onClick={() => setPage(p as number)}
                  className={`px-3 py-1.5 rounded-lg border text-sm transition ${
                    safePage === p ? "bg-purple-600 text-white border-purple-600" : "hover:bg-purple-50"
                  }`}>{p}</button>
              )
            )}
          <button disabled={safePage === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-30 hover:bg-purple-50 transition">›</button>
          <button disabled={safePage === totalPages} onClick={() => setPage(totalPages)}
            className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-30 hover:bg-purple-50 transition">»</button>
        </div>
      </div>
    </div>
  );
}

/* ─── UI PARTS ─── */
function Th({ children }: any) {
  return <th className="text-left px-5 py-4 font-semibold">{children}</th>;
}
function Td({ children, className = "" }: any) {
  return <td className={`px-5 py-3 ${className}`}>{children}</td>;
}
function StatusBadge({ status }: { status: string }) {
  const style =
    status === "Sangat Baik"
      ? "bg-green-100 text-green-700"
      : status === "Cukup"
      ? "bg-yellow-100 text-yellow-700"
      : "bg-red-100 text-red-700";
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${style}`}>
      {status}
    </span>
  );
}
function ExcelIcon(props: any) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="currentColor">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8.5 17l2-3-2-3h1.8l1.2 2 1.2-2H14l-2.1 3L14 17h-1.8l-1.2-2-1.2 2H8.5z"/>
    </svg>
  );
}
